import { useRothko } from "web/react/hooks/use-rothko/use-rothko";
import { CaptureType } from "web/redux/ducks/customer-capture-overlay";
import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import { trackKevelEvent } from "web/script/utils/ad-analytics";
import { getCampaignHasBeenDismissed } from "web/script/utils/block-modal-campaigns";
import {
    CampaignRawBranchLink,
    getBranchCampaignName,
    RawBranchLink,
} from "web/script/utils/branch-io";
import storage from "web/script/utils/storage";
import { URLQuery } from "web/script/utils/url";
import { WebToAppCampaignSerializer } from "web/types/serializers";

type CampaignType = "top_banner" | "branch_link" | "block_modal";
let fetchResolver;

export interface CampaignOptions {
    isIcon?: boolean;
    hasBeenBlocked?: boolean;
    hasBeenDismissed?: boolean;
    action?: string;
    contextType?: string;
    isAffiliate?: boolean;
    hasPromotion?: boolean;
    retailerExcludedFromApp?: boolean;
    heimdallFlags?: string[];
    inStock?: boolean;
    areaClicked?: string;
    productId?: number;
    linkId?: number | null;
    currency?: string | null;
    retailerSlug?: string | null;
    newUserGenerated?: boolean;
}

export interface CampaignDetails {
    branchLink: string;
    name: string;
    blockType?: string | null;
    titleKey?: string | null;
    componentType: string;
    captureType?: CaptureType | null;
    cheapestPriceComparison?: {
        isCheapestPrice: boolean;
        percentagePriceDifference: number;
    };
}

export interface WebToAppCampaign {
    isKevelEnabled: boolean;
    campaignDetails: CampaignDetails | undefined;
    onClick: () => void;
    onLoad: () => void;
    fetchCampaign: () => Promise<CampaignDetails | undefined>;
}

function getCampaignDetails(
    data: WebToAppCampaignSerializer,
    campaignType: CampaignType
): CampaignDetails | undefined {
    const validBranchLinks = campaignType === "top_banner" ? CampaignRawBranchLink : RawBranchLink;
    let branchLink = "";
    if (data.branch_link_name) {
        branchLink = validBranchLinks[data.branch_link_name];

        if (!branchLink) {
            console.error("Not a valid branch_link_name");
            return undefined;
        }
    }

    let cheapestPriceComparison;
    if (data.cheapest_price_comparison) {
        cheapestPriceComparison = {
            isCheapestPrice: data.cheapest_price_comparison.is_cheapest_price,
            percentagePriceDifference: data.cheapest_price_comparison.percentage_price_difference,
        };
    }

    return {
        branchLink,
        titleKey: data.title_key,
        blockType: data.block_type,
        componentType: data.component_type,
        name: getBranchCampaignName(branchLink),
        captureType: data.capture_type ? (data.capture_type as CaptureType) : null,
        cheapestPriceComparison: cheapestPriceComparison,
    };
}

export default function useWebToAppCampaign(
    campaignType: CampaignType,
    options: CampaignOptions = {},
    enabled = true,
    campaignName = ""
): WebToAppCampaign {
    const endpoint = "modules/web_to_app_campaign";
    const isKevelEnabled = Boolean(environment.getFeature("mg_kevel_campaigns"));

    function _getQuery(): URLQuery {
        const query: URLQuery = {
            campaignType: campaignType,
            feedType: environment.get("feedType"),
            pageType: environment.get("pageType"),
            pageSubType: environment.get("pageSubType"),
            pageValue: environment.get("pageValue")?.toString(),
            initialPageType: analytics.getInitialPageType(),
            initialPageSubType: analytics.getInitialSubPageType(),
            continueClicked: storage.get("continueClicked", false, true),
            ...new URLSearchParams(Object.entries(options)).toJSON(),
        };

        if (campaignName) {
            const { hasBeenDismissed, dismissedCount } = getCampaignHasBeenDismissed(campaignName);
            query.hasBeenDismissed = hasBeenDismissed.toString();
            query.dismissedCount = dismissedCount.toString();
        }

        return query;
    }

    const initialQuery = _getQuery();
    const { data, run, isFetched } = useRothko<WebToAppCampaignSerializer>(endpoint, initialQuery, {
        enabled: isKevelEnabled && enabled,
        rothko: { cache: true },
    });

    let campaignDetails;
    if (data) {
        if (data.session_variable_key && data.session_variable_value) {
            storage.set(data.session_variable_key, data.session_variable_value, true);
        }
        campaignDetails = getCampaignDetails(data, campaignType);
    }

    function onClick(): void {
        if (data && data.click_url) {
            trackKevelEvent(data.click_url, "true");
        }
    }

    function onLoad(): void {
        if (data && data.impression_url) {
            trackKevelEvent(data.impression_url, "false");
        }
    }

    function fetchCampaign(): Promise<CampaignDetails | undefined> {
        return new Promise((resolve) => {
            if (isKevelEnabled) {
                const query = _getQuery();
                run(query);
                fetchResolver = resolve;
            } else {
                resolve(undefined);
            }
        });
    }

    if (isFetched && fetchResolver) {
        fetchResolver(campaignDetails);
        fetchResolver = null;
    }

    return {
        isKevelEnabled,
        campaignDetails,
        onClick,
        onLoad,
        fetchCampaign,
    };
}
